import { useState, type ReactElement } from 'react';

import { useAuth } from '@/nucleo/auth/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { ErrorState } from '@/ui/ErrorState';

import {
  AMBIENTES,
  podeVerConector,
  ROTULO_DO_AMBIENTE,
  TAMANHO_PAGINA_PADRAO,
  type Ambiente,
  type DadosAmbiente,
  type FiltroTentativas,
  type ResultadoTeste,
} from './contrato';
import { useAmbientes, useGravarAmbiente, useTentativas, useTestarConexao } from './dados';
import { FormularioDoAmbiente } from './FormularioDoAmbiente';
import { Notificacao, type TomDaNotificacao } from '@/ui/Notificacao';
import { RECUSA_DO_CONECTOR } from './recusaDoConector';
import { TabelaDeTentativas } from './TabelaDeTentativas';

/**
 * `/admin/integracoes` — o painel do conector de login gov.br.
 *
 * Ate esta tela existir, o item de menu abria a tela de modulo em construcao —
 * e mal, porque ele e o unico item de fase 1 do sistema servido por uma tela
 * feita para fase futura: a frase saia sem dizer em que fase o modulo entra.
 *
 * O que a tela mostra e o que docs/CONECTOR-GOVBR.md, secao 5, lista, e o que o
 * modulo `govbr` da API expoe (apps/api/src/modulos/govbr/govbr.controller.ts).
 *
 * TRES DECISOES DE ARRANJO, E O PORQUE DE CADA UMA:
 *
 * 1. UMA ABA POR AMBIENTE. Sao tres linhas com a mesma forma, e o administrador
 *    edita uma de cada vez. Empilhar os tres formularios daria uma pagina de
 *    rolagem longa em que e facil salvar o ambiente errado; a aba deixa
 *    explicito qual esta sendo editado, e cabe em 375 px.
 *
 * 2. O HISTORICO E UM SO, COM FILTRO PROPRIO, FORA DAS ABAS. A pergunta que ele
 *    responde e quase sempre comparativa — "o que aconteceu por ultimo,
 *    aqui e nos outros" — e amarra-lo a aba obrigaria a trocar de aba para
 *    responder. O filtro por ambiente continua ali para quem quiser o recorte.
 *
 * 3. A RECUSA VEM ANTES DA BUSCA. `podeVerConector` compara o papel antes de
 *    qualquer requisicao. E a armadilha do `GESTOR_ADMIN`: ele atravessa a
 *    `RotaProtegida`, porque a area de administracao o aceita
 *    (nucleo/navegacao/mapa.ts), mas as duas tabelas do conector so abrem para
 *    `ADMIN` (db/migrations/0004_rls_baseline.sql:121-128). Sem esta conferencia
 *    ele veria o estado VAZIO, que mentiria: "nao ha nada aqui" quando a verdade
 *    e "voce nao alcanca isto".
 */

interface Aviso {
  tom: TomDaNotificacao;
  mensagem: string;
}

/** O resultado do teste de cada ambiente, enquanto a pessoa esta na tela. */
type ResultadosPorAmbiente = Partial<Record<Ambiente, ResultadoTeste>>;

export function ConectorGovBrPage(): ReactElement {
  const { usuario } = useAuth();

  const [abaAtual, setAbaAtual] = useState<Ambiente>(AMBIENTES[0]);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [resultados, setResultados] = useState<ResultadosPorAmbiente>({});
  const [filtro, setFiltro] = useState<FiltroTentativas>({
    pagina: 1,
    tamanho: TAMANHO_PAGINA_PADRAO,
  });

  // A conferencia de papel decide se as consultas chegam a sair. Os ganchos sao
  // chamados sempre — a ordem deles nao pode variar entre renders —, e e o
  // `habilitado` que impede a requisicao de quem ja se sabe sem direito.
  const alcanca = podeVerConector(usuario);

  const ambientes = useAmbientes(alcanca);
  const tentativas = useTentativas(filtro, alcanca);
  const gravacao = useGravarAmbiente();
  const teste = useTestarConexao();

  if (!alcanca) {
    return <SemAcessoAoConector />;
  }

  const ambienteDaAba = ambientes.data?.find((linha) => linha.ambiente === abaAtual);

  function gravar(ambiente: Ambiente, dados: DadosAmbiente) {
    setAviso(null);
    gravacao.mutate(
      { ambiente, dados },
      {
        onSuccess: () =>
          setAviso({
            tom: 'sucesso',
            mensagem: `A parametrização de ${ROTULO_DO_AMBIENTE[ambiente]} foi salva.`,
          }),
        // A frase vem do cliente da API, que ja traduziu a resposta para
        // portugues (nucleo/auth/mensagens.ts). Nada tecnico chega aqui.
        onError: (erro) => setAviso({ tom: 'falha', mensagem: erro.aviso }),
      },
    );
  }

  function testar(ambiente: Ambiente) {
    setAviso(null);
    teste.mutate(ambiente, {
      onSuccess: (resultado) => {
        setResultados((anteriores) => ({ ...anteriores, [ambiente]: resultado }));
        // `sucesso: false` NAO e falha do sistema: e o emissor que nao atendeu, e
        // a API responde 200 com o motivo legivel (criterio E4.3). O aviso
        // carrega esse motivo em vez de uma frase generica.
        setAviso(
          resultado.sucesso
            ? {
                tom: 'sucesso',
                mensagem: `O emissor de ${ROTULO_DO_AMBIENTE[ambiente]} respondeu como esperado.`,
              }
            : {
                tom: 'falha',
                mensagem: resultado.mensagem ?? 'O teste de conexão não teve sucesso.',
              },
        );
      },
      onError: (erro) => setAviso({ tom: 'falha', mensagem: erro.aviso }),
    });
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Conector gov.br</h1>
        <p className="text-sm text-muted-foreground">
          Parametrização da entrada pelo gov.br, por ambiente, e o histórico das tentativas de
          integração. O segredo do cliente não fica aqui: ele vive no cofre de segredos do ambiente,
          e quem o usa é o provedor de identidade.
        </p>
      </header>

      {aviso && (
        <Notificacao tom={aviso.tom} mensagem={aviso.mensagem} aoFechar={() => setAviso(null)} />
      )}

      <EstadoDaTela
        carregando={ambientes.isPending}
        erro={ambientes.error ?? null}
        aoTentarDeNovo={() => void ambientes.refetch()}
        descricao="a parametrização dos ambientes"
        recusa={RECUSA_DO_CONECTOR}
      >
        <Tabs value={abaAtual} onValueChange={(valor) => setAbaAtual(valor as Ambiente)}>
          <TabsList className="w-full sm:w-auto">
            {AMBIENTES.map((ambiente) => (
              <TabsTrigger key={ambiente} value={ambiente} className="flex-1 sm:flex-none">
                {ROTULO_DO_AMBIENTE[ambiente]}
              </TabsTrigger>
            ))}
          </TabsList>

          {(ambientes.data ?? []).map((linha) => (
            <TabsContent key={linha.ambiente} value={linha.ambiente} className="mt-4">
              <FormularioDoAmbiente
                ambiente={linha}
                aoGravar={(dados) => gravar(linha.ambiente, dados)}
                gravando={gravacao.isPending && gravacao.variables?.ambiente === linha.ambiente}
                aoTestar={() => testar(linha.ambiente)}
                testando={teste.isPending && teste.variables === linha.ambiente}
                resultado={resultados[linha.ambiente] ?? null}
              />
            </TabsContent>
          ))}
        </Tabs>
      </EstadoDaTela>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Últimas tentativas de integração</CardTitle>
          <CardDescription>
            Cada entrada pelo gov.br e cada teste de conexão deixam uma linha aqui, com a etapa em
            que parou e o motivo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EstadoDaTela
            carregando={tentativas.isPending}
            erro={tentativas.error ?? null}
            aoTentarDeNovo={() => void tentativas.refetch()}
            descricao="o histórico de tentativas"
            esqueleto={{ colunas: 6 }}
            recusa={RECUSA_DO_CONECTOR}
          >
            <TabelaDeTentativas
              pagina={tentativas.data}
              carregando={tentativas.isFetching}
              filtro={filtro}
              aoMudarFiltro={setFiltro}
              // Sem emissor gravado, o teste falharia na hora com "informe o
              // endereco do emissor" — e o estado vazio estaria oferecendo um
              // botao que ja se sabe que nao leva a lugar nenhum. Nesse caso ele
              // fica sem acao, e quem quiser entender sobe os olhos: o botao do
              // formulario esta desabilitado, com o motivo escrito ao lado.
              aoTestarConexao={ambienteDaAba?.issuer_url ? () => testar(abaAtual) : undefined}
            />
          </EstadoDaTela>
        </CardContent>
      </Card>
    </div>
  );
}

export default ConectorGovBrPage;

/**
 * A recusa antes de buscar, para quem chegou à área de administração com um papel
 * que não alcança o conector. Fala o mesmo que o 403 da busca, porque os dois
 * leem `RECUSA_DO_CONECTOR` (ver `recusaDoConector.tsx`).
 */
function SemAcessoAoConector(): ReactElement {
  return (
    <ErrorState
      variant="forbidden"
      title={RECUSA_DO_CONECTOR.titulo}
      description={RECUSA_DO_CONECTOR.descricao}
      primaryAction={RECUSA_DO_CONECTOR.acao}
    />
  );
}
